import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { FORM_ERROR } from 'final-form';
import { TextField } from 'mui-rff';
import { Form } from 'react-final-form';
import ProdConfirmationDialog from '@components/ProdConfirmationDialog';

import { breadcrumbsActions } from '@store/breadcrumbs';
import { usePermissions } from '@store/auth';
import { showSuccessMessage } from '@store/dashboard';

import ApplicationPageTemplate from '@dashboardComponents/ApplicationPageTemplate/ApplicationPageTemplate';
import {
  addNewPlatform,
  getPlatformDetails,
  paymentOptionsPlatformsActions,
  selectSelectedPlatform,
  updatePlatform,
} from '../../store/payment-options-platforms';
import {
  addPlatform,
  editPlatform,
  PLATFORM_LIST_ROUTE,
} from '../../payment-options.breadcrumbs';

import FormSaveAndSubmit from '../../../../components/FormSaveAndSubmit';
import { Typography } from '../../../../components/styledComponents';

const initForm = {
  code: '',
  name: '',
};

export default function PlatformForm() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const selectedPlatform = useSelector(selectSelectedPlatform);

  const { id } = useParams();

  const permissions = usePermissions();
  const canEdit = permissions?.canEditPaymentOptions;

  // A view-only user can still open an existing record, but must not reach the
  // create form directly via its URL.
  useEffect(() => {
    if (id === undefined && !permissions?.canEditPaymentOptions) {
      navigate(PLATFORM_LIST_ROUTE);
    }
  }, [id, navigate, permissions]);

  const [isFetchingData, setIsFetchingData] = useState(true);
  const [formValue, setFormValue] = useState(initForm);
  const [formSuccessMsg, setFormSuccessMsg] = useState('');
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const [openDialog, setOpenDialog] = useState({ show: false });

  useEffect(() => {
    setIsFetchingData(true);
    dispatch(breadcrumbsActions.showMenu(true));

    if (id !== undefined) {
      dispatch(breadcrumbsActions.setBreadcrumbs(editPlatform(id)));
      Promise.all([dispatch(getPlatformDetails(id))]).then(() =>
        setIsFetchingData(false),
      );
    } else {
      dispatch(breadcrumbsActions.setBreadcrumbs(addPlatform));
      Promise.all([
        dispatch(paymentOptionsPlatformsActions.resetSelectedPlatform()),
      ]).then(() => setIsFetchingData(false));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (id !== undefined) {
      setFormValue(selectedPlatform);
    } else {
      setFormValue(initForm);
    }
  }, [selectedPlatform, id]);

  const onSubmit = (values) => {
    setFormSuccessMsg('');
    setFormErrorMsg('');

    const payload = {
      id,
      code: values.code,
      name: values.name,
    };

    return dispatch(
      id !== undefined ? updatePlatform(payload) : addNewPlatform(payload),
    ).then((data) => {
      if (data.payload && data.payload.status === 'SUCCESS') {
        if (id === undefined) {
          dispatch(
            showSuccessMessage(
              `Platform ${id !== undefined ? 'updated' : 'added'} successfully`,
            ),
          );
          navigate(PLATFORM_LIST_ROUTE);
        } else {
          setFormSuccessMsg('Platform has been updated successfully');
          setFormValue(values); // needed to disable buttons after successful submit
        }
      } else {
        setFormErrorMsg(data.payload.message);
      }
    });
  };

  const validate = (values) => {
    if (!values.name) return { [FORM_ERROR]: 'Platform Name is required' };
    if (!values.code) return { [FORM_ERROR]: 'Platform Code is required' };
    return {};
  };

  const handlePreSubmit = (e) => {
    e.preventDefault();
    setOpenDialog({ show: true }); // open the confirmation dialog if prod
  };

  return (
    <ApplicationPageTemplate
      title={id !== undefined ? selectedPlatform.name : 'New Platform'}
      pageReady={!isFetchingData}>
      {!isFetchingData && (
        <Form
          onSubmit={onSubmit}
          initialValues={formValue}
          validate={validate}
          render={({ handleSubmit, pristine, invalid, form }) => (
            <form
              onSubmit={(e) =>
                process.env.REACT_APP_ENV !== 'production'
                  ? handleSubmit(e)
                  : handlePreSubmit(e)
              }
              noValidate>
              <ProdConfirmationDialog
                openDialog={openDialog}
                yesHandler={handleSubmit}
              />
              <Grid
                container
                spacing={1}
                style={{
                  alignContent: 'center',
                  justifyContent: 'space-between',
                }}>
                <Grid
                  item
                  xs={12}
                  md={8}
                  style={{ flex: 2 }}>
                  {formSuccessMsg && (
                    <Alert
                      onClose={() => {
                        setFormSuccessMsg('');
                      }}
                      severity="success">
                      {formSuccessMsg}
                    </Alert>
                  )}
                  {formErrorMsg && (
                    <Alert
                      onClose={() => {
                        setFormErrorMsg('');
                      }}
                      severity="error">
                      {formErrorMsg}
                    </Alert>
                  )}
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={4}
                  style={{ display: 'flex' }}>
                  <FormSaveAndSubmit
                    id={id}
                    resetDisabled={pristine}
                    submitDisabled={pristine || invalid || !canEdit}
                    handleReset={() => {
                      setFormSuccessMsg('');
                      setFormErrorMsg('');
                      form.reset();
                    }}
                  />
                </Grid>
              </Grid>
              <Box
                style={{
                  marginTop: '20px',
                  padding: '32px',
                  border: '1px solid #c4c4c4',
                  borderRadius: '4px',
                }}>
                <Typography
                  className="form-header"
                  variant="formHeader">
                  Details
                </Typography>
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="flex-start"
                  spacing={2}>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <TextField
                      required
                      aria-label="platform-name"
                      name="name"
                      variant="outlined"
                      label="Name"
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}>
                    <TextField
                      required
                      aria-label="platform-code"
                      name="code"
                      variant="outlined"
                      label="Code"
                      helperText="Sample: websitewmp"
                      disabled={id !== undefined}
                    />
                  </Grid>
                </Grid>
              </Box>
            </form>
          )}
        />
      )}
    </ApplicationPageTemplate>
  );
}
